import React from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import FilterListIcon from '@material-ui/icons/FilterList';
import TextField from '@material-ui/core/TextField';
import { DatePicker } from '@material-ui/pickers';
import { Tooltip } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MTableToolbar } from 'material-table';
import useRouter from 'use-react-router';
import useReactRouter from 'use-react-router';
import { t } from 'ttag';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import queryString from 'query-string';
import DataTable from '../../../components/DataTable';
import {
  CERTIFICATION_ATHLETE_STATUS_TITLE,
  CONTRIBUTION_STATUS_OPTIONS,
  RANK_PRIORITY_MAP,
  USER_STATUS,
  USER_TYPES,
} from '../../../utils/enums';
import useApi, { MyClubCApi } from '../../../api';
import useUser from '../../../auth/hooks';
import {
  formatPublicDate,
  formatRank,
  getCurrentYear,
  isIntersection,
  nexYear,
  prepareParam,
} from '../../../utils/helpers';
import { useNavigator } from '../../../components/Navigator';
import { extractDataError } from '../../../utils/errors';
import useNotifier from '../../../components/Messages/hooks';
import useEditForm from '../../../hooks/useEditForm';
import MyChip from '../../../components/MyChip';
import Loading from '../../../components/Loading';
import { SearchInput } from '../../../components/MaterialToolBarSearchInput';

const ListView = () => {
  const { match } = useReactRouter();
  const certificationsId = match.params.id;
  const api = useApi();
  const client = useApolloClient();
  const newApi = new MyClubCApi(client);
  const navigate = useNavigator();
  const { user } = useUser();
  const pushMessage = useNotifier();
  const { history } = useRouter();
  const [loading, setLoading] = React.useState(false);
  const [trainers, setTrainers] = React.useState([]);
  const [certificationAthletes, setCertificationAthletes] = React.useState([]);
  const searchParams = new URLSearchParams(history.location.search);
  const [pageState, setPageState] = React.useState({
    query: searchParams.get('query') || undefined,
    trainerId: searchParams.get('trainerId') || undefined,
    contribution_status: parseInt(searchParams.get('contribution_status'), 10),
    contribution_year:
      searchParams.get('contribution_year') || String(getCurrentYear()),
  });

  const {
    dataCert,
    loading: certLoading,
    refetch,
  } = useEditForm({
    schema: api.schema.certifications,
    router: null,
    variables: { inputFilter: { id: Number(certificationsId) } },
  });

  const [closeCertifications] = useMutation(
    api.schema.certifications.close()[0],
    {
      onCompleted: (d) => {
        const { closeCertifications: res } = d;
        if (res.ok) {
          pushMessage({ text: 'Реєстрацію закрито' });
          refetch();
        } else {
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );
  const [openCertifications] = useMutation(
    api.schema.certifications.open()[0],
    {
      onCompleted: (d) => {
        const { openCertifications: res } = d;
        if (res.ok) {
          pushMessage({ text: 'Реєстрацію відкрито' });
          refetch();
        } else {
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );

  const updateHistory = (searchParams) => {
    history.replace({
      search: `?${queryString.stringify(searchParams)}`,
    });
  };

  const onChangeYear = async (e) => {
    setPageState({ ...pageState, contribution_year: String(e.getFullYear()) });
  };

  const fetchContributionAthletes = async () => {
    setLoading(true);
    updateHistory(prepareParam(pageState));
    const data = await newApi.getCertificationAthletes({
      query: pageState.query,
      trainerId: pageState.trainerId,
      certificationsId: Number(certificationsId),
      contributionStatus: pageState.contribution_status,
      contributionYear: pageState.contribution_year,
    });
    const trainersData = await newApi.getTrainers({
      status: USER_STATUS.active,
      trainerIds: data.map((r) => r.trainerId),
    });
    setCertificationAthletes(data);
    setTrainers(trainersData);
    setLoading(false);
  };

  React.useEffect(() => {
    fetchContributionAthletes();
  }, [pageState]);

  const handleOndelete = async (data) => {
    const res = await newApi.deleteCertificationAthlete({
      certificationsId: Number(data.id),
    });
    if (res.ok) {
      pushMessage({ text: 'Успішно видалено' });
    } else {
      const dataError = extractDataError(res.errors);
      pushMessage({ text: dataError });
    }
    await fetchContributionAthletes();
  };

  if (loading || certLoading) return <Loading />;

  const columns = [
    { title: t`Id`, field: 'id', editable: 'never' },
    {
      title: t`Імя тренера`,
      field: 'trainer_name',
      render: ({ trainer }) => (trainer && trainer.fullName) || '',
      customSort: (a, b) =>
        a.trainer.fullName.length - b.trainer.fullName.length,
      searchable: true,
      editable: 'never',
    },
    {
      title: t`Імя атлета`,
      field: 'fullName',
      filtering: false,
      render: ({ athlete, contributions }) =>
        contributions.filter((c) => c.year === pageState.contribution_year)
          .length !== 0 ? (
          (athlete && athlete.fullName) || ''
        ) : (
          <Tooltip title={t`Не здав членський внесок`}>
            <span>
              <MyChip
                label={athlete.fullName}
                color='#db0a0a'
                variant='outlined'
                onClick={() =>
                  navigate(`${api.router.athletes.edit(athlete.id)}?tab=1`)
                }
              />
            </span>
          </Tooltip>
        ),
    },
    {
      title: t`Кю`,
      field: 'rank',
      render: ({ rank }) => formatRank(rank) || '',
      customSort: (a, b) =>
        (RANK_PRIORITY_MAP[a.rank] || 0) - (RANK_PRIORITY_MAP[b.rank] || 0),
    },
    {
      title: t`Статус`,
      field: 'status',
      render: ({ statusExamination }) =>
        CERTIFICATION_ATHLETE_STATUS_TITLE[statusExamination],
      sorting: false,
    },
  ];

  const countAthletes = new Set(certificationAthletes.map((r) => r.athleteId)).size;

  return (
    <Paper>
      <DataTable
        search={false}
        sorting={true}
        isLoading={loading}
        title={
          <div>
            {dataCert ? dataCert.name : null}
            <br />
            {`Екзаменатори: ${dataCert ? dataCert.examiners : null}`}
            <br />
            {`${formatPublicDate(
              dataCert ? dataCert.startedAt : null
            )} - ${formatPublicDate(dataCert ? dataCert.closedAt : null)}`}
            <br />
            <span>
              {t`Всього спортсменів: `}
              <b>{countAthletes}</b>
            </span>
          </div>
        }
        columns={columns}
        data={certificationAthletes}
        onAdd={
          (isIntersection(user.types, [
            USER_TYPES.superManager,
            USER_TYPES.manager,
          ]) ||
            user.isAdmin ||
            (isIntersection(user.types, [USER_TYPES.trainer]) &&
              (dataCert ? dataCert.status : null) == 1)) &&
          (dataCert ? dataCert.status : null) != 3
            ? () =>
                navigate(api.router.certifications.addAthlete(certificationsId))
            : null
        }
        onClosed={
          (isIntersection(user.types, [
            USER_TYPES.superManager,
            USER_TYPES.manager,
          ]) ||
            user.isAdmin) &&
          (dataCert ? dataCert.status : null) == 1 &&
          (dataCert ? dataCert.status : null) != 3
            ? () => closeCertifications({ variables: { id: certificationsId } })
            : null
        }
        onOpen={
          (isIntersection(user.types, [
            USER_TYPES.superManager,
            USER_TYPES.manager,
          ]) ||
            user.isAdmin) &&
          (dataCert ? dataCert.status : null) == 2 &&
          (dataCert ? dataCert.status : null) != 3
            ? () => openCertifications({ variables: { id: certificationsId } })
            : null
        }
        onProtocol={
          (dataCert ? dataCert.status : null) != 3
            ? () =>
                navigate(api.router.certifications.Protocol(certificationsId))
            : null
        }
        onStatement={
          (dataCert ? dataCert.status : null) != 3
            ? () =>
                navigate(api.router.certifications.Statement(certificationsId))
            : null
        }
        onStatistics={() =>
          navigate(api.router.certifications.Statistics(certificationsId))
        }
        onEdit={(e, d) =>
          navigate(
            api.router.certifications.editAthlete(certificationsId, d.id)
          )
        }
        onDelete={handleOndelete}
        isDeletableFn={(d) =>
          !(
            dataCert?.status == 2 ||
            d.statusStatementAthlete == 3 ||
            d.statusProtocol == 2
          )
        }
        isEditableFn={() => !(dataCert?.status == 2)}
        components={{
          Toolbar: (props) => (
            <div style={{ flexGrow: 1 }}>
              <MTableToolbar {...props} />
              <Grid
                container
                spacing={2}
                style={{ marginLeft: '10px' }}
                justifyContent='flex-start'
                alignItems='center'
              >
                <Grid item>
                  <Tooltip title={t`Виберіть фільтр`}>
                    <FilterListIcon />
                  </Tooltip>
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={CONTRIBUTION_STATUS_OPTIONS}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Статус внеску`}
                        variant='outlined'
                      />
                    )}
                    value={CONTRIBUTION_STATUS_OPTIONS.find(
                      (item) =>
                        item && item.id === pageState.contribution_status
                    )}
                    onChange={(e, value) =>
                      setPageState({
                        ...pageState,
                        contribution_status: value && value.id,
                      })
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <DatePicker
                    size={'small'}
                    variant='inline'
                    label={t`Рік внеску`}
                    InputAdornmentProps={{ position: 'start' }}
                    inputVariant='outlined'
                    openTo='year'
                    format='yyyy'
                    views={['year']}
                    value={pageState.contribution_year}
                    cancelLabel='Відмінити'
                    maxDate={nexYear()}
                    onChange={onChangeYear}
                  />
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={trainers}
                    getOptionLabel={(option) => option.fullInitials}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть тренера`}
                        variant='outlined'
                      />
                    )}
                    value={trainers.find(
                      (item) => item && item.id === pageState.trainerId
                    )}
                    onChange={(e, value) =>
                      setPageState({
                        ...pageState,
                        trainerId: value && value.id,
                      })
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <SearchInput
                    label={t`Пошук спортсмена`}
                    value={pageState.query}
                    onChange={(value) =>
                      setPageState({ ...pageState, query: value })
                    }
                  />
                </Grid>
              </Grid>
            </div>
          ),
        }}
      />
    </Paper>
  );
};

export default ListView;
